/**
 * Map Utilities Module
 * 
 * Shared utility functions for map controllers to eliminate code duplication.
 * These methods were previously duplicated across multiple modules.
 */

import Logger from '../../lib/logger';

export class MapUtilities {
  
  /**
   * Ensure proper layer ordering (markers and layers in front, circle behind)
   * 
   * @param {Object} controller - The map controller instance
   */
  static ensureProperLayerOrdering(controller) {
    try {
      // Bring bids layer to front
      if (controller.bids && controller.map.hasLayer(controller.bids)) {
        controller.bids.eachLayer(layer => {
          if (layer.bringToFront) {
            layer.bringToFront();
          }
        });
      }
      
      // Bring geojson layer to front
      if (controller.geojson && controller.map.hasLayer(controller.geojson)) {
        if (controller.geojson.bringToFront) {
          controller.geojson.bringToFront();
        }
      }
      
      // Bring KML layers to front
      if (controller.mapklayer && controller.map.hasLayer(controller.mapklayer)) {
        controller.mapklayer.eachLayer(layer => {
          if (layer.bringToFront) {
            layer.bringToFront();
          }
        });
      }
      
      Logger.debug('Layer ordering ensured');
    } catch (error) {
      Logger.error('Failed to ensure proper layer ordering', error, { 
        hasBids: !!controller.bids,
        hasGeojson: !!controller.geojson,
        hasMapklayer: !!controller.mapklayer
      });
    }
  }
  
  /**
   * Auto-update bid providers tab if it's currently active
   *
   * @param {Object} controller - The map controller instance
   * @param {number} delay - Delay in milliseconds before updating (default: 500ms)
   */
  static autoUpdateBidProvidersTab(controller, delay = 500) {
    try {
      setTimeout(() => {
        // Use Stimulus target if available, fallback to getElementById
        const bidProvidersPane = controller.hasBidProvidersPaneTarget
          ? controller.bidProvidersPaneTarget
          : document.getElementById('bid-providers');
        if (bidProvidersPane && bidProvidersPane.classList.contains('active')) {
          if (typeof controller.loadBidProviders === 'function') {
            controller.loadBidProviders();
            Logger.debug('Auto-updated bid providers tab');
          } else {
            Logger.warn('loadBidProviders method not found on controller');
          }
        }
      }, delay);
    } catch (error) {
      Logger.error('Failed in autoUpdateBidProvidersTab', error, { delay });
    }
  }
  
  /**
   * Create a cache key from bounds and filters
   * 
   * @param {Object} bounds - Leaflet bounds object
   * @param {Object} options - Additional options for cache key generation
   * @param {boolean} options.showRecentBidsOnly - Whether showing only recent bids
   * @param {Object} options.currentProviderFilter - Current provider filter object
   * @param {number} options.precision - Rounding precision (default: 0.01 ≈ 1km)
   * @returns {string} Cache key
   */
  static createCacheKey(bounds, options = {}) {
    try {
      const precision = options.precision || 0.01;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      
      const roundedBounds = [
        Math.round(sw.lat / precision) * precision,
        Math.round(sw.lng / precision) * precision,
        Math.round(ne.lat / precision) * precision,
        Math.round(ne.lng / precision) * precision
      ].join(',');
      
      // Include filter state in cache key
      const filterKey = `recent:${options.showRecentBidsOnly ?? true}`;
      const providerKey = options.currentProviderFilter ? 
        `provider:${options.currentProviderFilter.id}` : 'all';
      
      const cacheKey = `${roundedBounds}|${filterKey}|${providerKey}`;
      Logger.debug('Created cache key', { cacheKey, options });
      return cacheKey;
    } catch (error) {
      Logger.error('Failed to create cache key', error, { bounds, options });
      return 'error-cache-key';
    }
  }
  
  /**
   * Format bounds as string for API calls
   * 
   * @param {Object} bounds - Leaflet bounds object
   * @returns {string} Formatted bounds string "sw_lat,sw_lng,ne_lat,ne_lng"
   */
  static formatBoundsString(bounds) {
    try {
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const formatted = `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`;
      Logger.debug('Formatted bounds string', { formatted });
      return formatted;
    } catch (error) {
      Logger.error('Failed to format bounds string', error, { bounds });
      return '';
    }
  }
  
  /**
   * Validate address data has required coordinates
   * 
   * @param {Object} address - Address object to validate
   * @returns {boolean} True if address has valid coordinates
   */
  static isValidAddress(address) {
    try {
      const result = address && 
             typeof address.latitude !== 'undefined' && 
             typeof address.longitude !== 'undefined' &&
             !isNaN(parseFloat(address.latitude)) &&
             !isNaN(parseFloat(address.longitude));
      Logger.debug('Address validation result', { isValid: result, address });
      return result;
    } catch (error) {
      Logger.warn('Error validating address', { error, address });
      return false;
    }
  }
  
  /**
   * Extract addresses from various data formats
   * 
   * @param {*} jsonData - Data in various formats (object with addresses array, or array directly)
   * @returns {Array} Array of addresses
   */
  static extractAddressesFromData(jsonData) {
    try {
      if (jsonData && jsonData.addresses && Array.isArray(jsonData.addresses)) {
        Logger.debug('Extracted addresses from object format', { count: jsonData.addresses.length });
        return jsonData.addresses;
      } else if (Array.isArray(jsonData)) {
        Logger.debug('Extracted addresses from array format', { count: jsonData.length });
        return jsonData;
      } else {
        Logger.warn('Invalid data format received for address extraction', { jsonData });
        return [];
      }
    } catch (error) {
      Logger.error('Error extracting addresses from data', error, { jsonData });
      return [];
    }
  }
  
  /**
   * Debounce a function call
   * 
   * @param {Function} fn - Function to debounce
   * @param {number} delay - Delay in milliseconds
   * @returns {Function} Debounced function
   */
  static debounce(fn, delay = 100) {
    try {
      let timeout;
      const debounced = function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
      };
      
      // Add cancel method for more control
      debounced.cancel = function() {
        clearTimeout(timeout);
        timeout = undefined;
      };
      
      return debounced;
    } catch (error) {
      Logger.error('Failed to create debounce function', error, { fn, delay });
      return (fn) => fn;
    }
  }
  
  /**
   * Escape HTML to prevent XSS attacks
   * 
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   */
  static escapeHtml(text) {
    try {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    } catch (error) {
      Logger.error('Failed to escape HTML', error, { text });
      return '';
    }
  }
  
  /**
   * Log performance metric
   * 
   * @param {string} operation - Name of the operation
   * @param {number} startTime - Start time from performance.now()
   */
  static logPerformance(operation, startTime) {
    try {
      const duration = (performance.now() - startTime).toFixed(2);
      Logger.info(`${operation}: ${duration}ms`);
    } catch (error) {
      Logger.error('Failed to log performance metric', error, { operation, startTime });
    }
  }

  /**
   * Handle fetch errors gracefully with improved error categorization
   * 
   * @param {Object} controller - The map controller instance
   * @param {Error} error - The error object
   */
  static handleFetchError(controller, error) {
    const errorMessage = error.message || error.toString();

    // Categorize errors and show appropriate messages
    if (errorMessage.includes('500')) {
      controller.showInfoMessage(
        'Search area was too large and has been automatically adjusted. Results may be limited.',
        'info'
      );
    } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
      controller.showErrorMessage('Network connection error. Please check your internet connection.');
    } else if (errorMessage.includes('404')) {
      controller.showErrorMessage('The requested data could not be found.');
    } else if (errorMessage.includes('timeout')) {
      controller.showErrorMessage('Request timed out. Please try again.');
    } else {
      controller.showErrorMessage('Failed to load map data. Please try again.');
    }

    // Log detailed error for debugging
    Logger.error('Detailed error information', {
      message: errorMessage,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
  }
}